'use client';

import {
  UhxBetaIcon,
  UhxInfoDamage,
  UhxInfoReplacement,
  UhxInfoShield,
  UhxInfoSupport,
  UhxInfoWeightLoss,
  UhxInfoPregnancy,
} from 'assets/icons';
import { useTranslations } from 'next-intl';
import { UhxShopifyMap } from 'content/products/uhx';
import {
  CurrencySymbol,
  getCurrencySymbolFromEnum,
  useFormatPrice,
} from 'hooks/pricing/useFormattedPrice';
import { Currency } from 'utils/Currency';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { CustomImage } from '../custom-image';
import {
  ImageContainer,
  InfoModalContainer,
  InfoModalDescription,
  InfoModalTitle,
  PricingModal,
} from './layout';
import styles from './uhx.module.css';

/** Figma 2857:106502 card art, cropped to the frames' visible window and staged in `cdn-upload/`. */
const art = (file: string): string =>
  getCompressedAssetUrl(`/web_v2/cart-info-modals/uhx-3/${file}`);
const srcSetW = (url: string, widths: number[]): string =>
  widths.map((w) => `${url}/w_${w} ${w}w`).join(', ');

type ChipKey =
  | 'replacements'
  | 'damage'
  | 'support'
  | 'tradeIn'
  | 'weightLoss'
  | 'theft';

/** Figma 2857:106247: chip origins as a share of the 704×420 card. */
const SCATTER: Record<ChipKey, { left: string; top: string }> = {
  weightLoss: { left: '9.1%', top: '13.6%' },
  damage: { left: '72%', top: '11.9%' },
  theft: { left: '4.1%', top: '45%' },
  tradeIn: { left: '74.4%', top: '46.4%' },
  replacements: { left: '8%', top: '78.6%' },
  support: { left: '66.8%', top: '79.3%' },
};
const SCATTER_ORDER: ChipKey[] = [
  'weightLoss',
  'damage',
  'theft',
  'tradeIn',
  'replacements',
  'support',
];
/** Figma 2857:106464 wrap order. */
const WRAP_ORDER: ChipKey[] = [
  'replacements',
  'damage',
  'support',
  'tradeIn',
  'weightLoss',
  'theft',
];

type ItemKey =
  | 'damage'
  | 'replacements'
  | 'theft'
  | 'weightLoss'
  | 'pregnancy'
  | 'support';

const ITEMS: { key: ItemKey; icon: React.ReactNode; twoYear?: boolean }[] = [
  { key: 'damage', icon: <UhxInfoDamage /> },
  { key: 'replacements', icon: <UhxInfoReplacement /> },
  { key: 'theft', icon: <UhxInfoShield />, twoYear: true },
  { key: 'weightLoss', icon: <UhxInfoWeightLoss />, twoYear: true },
  { key: 'pregnancy', icon: <UhxInfoPregnancy />, twoYear: true },
  { key: 'support', icon: <UhxInfoSupport /> },
];

const ChipCheck = () => (
  <svg
    className={styles.chipCheck}
    viewBox="0 0 14.4 14.4"
    fill="none"
    aria-hidden
  >
    <path
      d="M3.2 7.52L5.84 10.08L11.2 4.48"
      stroke="currentColor"
      strokeWidth="1.36"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * UltrahumanX explainer (Figma 2857:106502). One markup for the 800px pricing
 * modal and the ~450px cart panel: a container query picks the desktop layout,
 * so the `.wide` art and scattered chips show only where the design has room.
 */
export const UhxCartInfoModal = () => {
  const t = useTranslations('ring');
  const CartClient = useCartStore(
    useShallow((s) => ({ store: s.store, currency: s.currency })),
  );
  const formatPriceWithComma = useFormatPrice();

  const { store, currency } = CartClient;
  // `CurrencySymbol` holds nodes; the ICU string needs text.
  const price = `${getCurrencySymbolFromEnum(
    currency ?? Currency.usd,
  )}${formatPriceWithComma(UhxShopifyMap[store as ShopifyStore].Year1.price)}`;
  const chip = (key: ChipKey) => (
    <span className={styles.chipLabel}>
      {t(`ring.infoModals.uhx.modal.chips.${key}`)}
    </span>
  );
  const alt = t('ring.UltrahumanX');

  return (
    <InfoModalContainer>
      <div className={styles.modal}>
        <div className={styles.headBlock}>
          <div className={styles.header}>
            <h3 className={styles.title}>{t('ring.infoModals.uhx.title')}</h3>
            <p className={styles.subtitle}>
              {t('ring.infoModals.uhx.modal.subtitle')}
            </p>
            <p className={styles.price}>
              {t('ring.infoModals.uhx.modal.price', { price })}
            </p>
          </div>
          <div className={styles.cardBlock}>
            <div className={styles.card}>
              <div className={styles.artWide}>
                <img
                  className={styles.art}
                  src={`${art('card-desktop.webp')}/w_1408`}
                  srcSet={srcSetW(art('card-desktop.webp'), [704, 1408, 2112])}
                  sizes="704px"
                  width={2839}
                  height={1694}
                  alt={alt}
                  loading="lazy"
                />
                {SCATTER_ORDER.map((key) => (
                  <span
                    key={key}
                    className={`${styles.chip} ${styles.chipScattered}`}
                    style={SCATTER[key]}
                  >
                    <ChipCheck />
                    {chip(key)}
                  </span>
                ))}
              </div>
              <div className={styles.artNarrow}>
                <img
                  className={styles.art}
                  src={`${art('card-mobile.webp')}/w_690`}
                  srcSet={srcSetW(
                    art('card-mobile.webp'),
                    [345, 690, 1035, 1380],
                  )}
                  sizes="(min-width: 1024px) 420px, calc(100vw - 48px)"
                  width={1699}
                  height={1329}
                  alt={alt}
                  loading="lazy"
                />
              </div>
              <div className={styles.covers}>
                {WRAP_ORDER.map((key) => (
                  <span key={key} className={styles.chip}>
                    <ChipCheck />
                    {chip(key)}
                  </span>
                ))}
              </div>
            </div>
            <p className={`${styles.body} ${styles.bodyWide}`}>
              {t('ring.infoModals.uhx.modal.body')}
            </p>
            <p className={`${styles.body} ${styles.bodyNarrow}`}>
              {t('ring.infoModals.uhx.modal.bodyMobile')}
            </p>
          </div>
        </div>

        <hr className={styles.divider} />

        <div className={styles.covered}>
          <h4 className={styles.coveredTitle}>
            {t('ring.infoModals.uhx.modal.coveredTitle')}
          </h4>
          <ul className={styles.list}>
            {ITEMS.map(({ key, icon, twoYear }) => (
              <li key={key} className={styles.item}>
                <span className={styles.itemIcon}>{icon}</span>
                <div className={styles.itemCopy}>
                  <div className={styles.itemTitleRow}>
                    <span className={styles.itemTitle}>
                      {t(`ring.infoModals.uhx.modal.items.${key}.title`)}
                    </span>
                    {twoYear ? (
                      <>
                        <span className={`${styles.note} ${styles.noteWide}`}>
                          {t('ring.infoModals.uhx.modal.note')}
                        </span>
                        <span className={`${styles.note} ${styles.noteNarrow}`}>
                          {t('ring.infoModals.uhx.modal.noteMobile')}
                        </span>
                      </>
                    ) : null}
                  </div>
                  <p className={styles.itemDescription}>
                    {t(`ring.infoModals.uhx.modal.items.${key}.description`)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </InfoModalContainer>
  );
};

export const HomeUhxCartInfoModal = () => {
  const t = useTranslations('ring');
  const CartClient = useCartStore(
    useShallow((s) => ({ store: s.store, currency: s.currency })),
  );
  const formatPriceWithComma = useFormatPrice();

  const { store, currency } = CartClient;
  const CurrencyCode = CurrencySymbol[currency ?? Currency.usd];

  const benefits = [
    {
      icon: <UhxInfoDamage />,
      title: t('ring.infoModals.uhx.home.damage.title'),
      description: t('ring.infoModals.uhx.home.damage.p1'),
    },
    {
      icon: <UhxInfoReplacement />,
      title: t('ring.infoModals.uhx.text.c.title'),
      description: t('ring.infoModals.uhx.text.c.p1'),
    },
    {
      icon: <UhxInfoShield />,
      title: t('ring.infoModals.uhx.home.environmental.title'),
      description: t('ring.infoModals.uhx.home.environmental.p1'),
      disclaimer: t('ring.infoModals.uhx.text.d.disclaimer'),
    },
    {
      icon: <UhxBetaIcon />,
      title: t('ring.infoModals.uhx.home.beta.title'),
      description: t('ring.infoModals.uhx.home.beta.p1'),
      disclaimer: t('ring.infoModals.uhx.text.e.disclaimer'),
    },
    {
      icon: <UhxInfoSupport />,
      title: t('ring.infoModals.uhx.text.f.title'),
      description: t('ring.infoModals.uhx.text.f.p1'),
    },
  ];

  return (
    <InfoModalContainer>
      <InfoModalTitle>
        <h3>{t('ring.infoModals.uhx.title')}</h3>
        <p>
          <>
            {CurrencyCode}
            {formatPriceWithComma(
              UhxShopifyMap[store as ShopifyStore].Year1.price,
            )}
          </>
          {t('ring.infoModals.uhx.subtitle1')}
        </p>
        <ImageContainer className="uhx-image-container">
          <CustomImage
            src="web_v2/cart-info-modals/uhx-2.png"
            width={1376}
            height={808}
            alt={t('ring.UltrahumanX')}
          />
        </ImageContainer>
      </InfoModalTitle>
      <InfoModalDescription>
        <p>{t('ring.infoModals.uhx.home.description')}</p>
      </InfoModalDescription>
      <div className={styles.uhxInfoDescription}>
        {benefits.map((benefit, index) => (
          <div key={index} className={styles.benefit}>
            {benefit.icon}
            <h3>{benefit.title}</h3>
            <p className={styles.description}>{benefit.description}</p>
            <p className={styles.disclaimer}>{benefit.disclaimer}</p>
          </div>
        ))}
      </div>
    </InfoModalContainer>
  );
};

export const UhxPricingModal = () => (
  <PricingModal className={styles.uhxPricingModal}>
    <UhxCartInfoModal />
  </PricingModal>
);

export const HomeUhxPricingModal = () => (
  <PricingModal>
    <HomeUhxCartInfoModal />
  </PricingModal>
);
